import { Link } from 'react-router-dom';
import { useTheme } from '../../../hooks/useTheme';
import { blogPosts } from '../../../mocks/blog';

const FEATURED_POSTS = blogPosts.slice(0, 3);

export default function BlogPreview() {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const sectionBg = isLight
    ? 'bg-gradient-to-br from-slate-50 via-white to-cyan-50/30'
    : 'bg-gradient-to-br from-navy-950 via-navy-900 to-navy-950';

  const cardBg = isLight ? 'bg-white border-slate-100' : 'bg-white/5 border-white/8';
  const titleColor = isLight ? 'text-navy-950' : 'text-white';
  const subColor = isLight ? 'text-navy-600' : 'text-white/55';
  const excerptColor = isLight ? 'text-navy-500' : 'text-white/50';
  const metaColor = isLight ? 'text-navy-400' : 'text-white/35';

  return (
    <section
      className={`relative py-24 px-4 sm:px-6 overflow-hidden ${sectionBg}`}
      id="blog-preview"
    >
      {/* Decorative blobs */}
      <div
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none opacity-[0.06]"
        style={{
          background: 'radial-gradient(circle, #00cfff 0%, transparent 70%)',
          transform: 'translate(25%, -25%)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none opacity-[0.05]"
        style={{
          background: 'radial-gradient(circle, #10b981 0%, transparent 70%)',
          transform: 'translate(-25%, 25%)',
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold tracking-widest uppercase mb-5 font-body"
            style={{
              borderColor: '#00cfff35',
              backgroundColor: '#00cfff0d',
              color: '#00cfff',
            }}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-article-line text-xs" />
            </div>
            Blog & Recursos
          </div>

          <h2 className={`font-heading text-4xl sm:text-5xl font-extrabold mb-4 leading-tight ${titleColor}`}>
            Aprende sobre{' '}
            <span className="text-electric">e-CF y DGII</span>
          </h2>

          <p className={`font-body text-base sm:text-lg leading-relaxed max-w-2xl mx-auto ${subColor}`}>
            Guías prácticas, normativa actualizada y consejos reales para que tu negocio cumpla con la DGII sin dolores de cabeza y sin sorpresas fiscales.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {FEATURED_POSTS.map((post, idx) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className={`group flex flex-col rounded-2xl border overflow-hidden transition-all duration-300 cursor-pointer ${cardBg}`}
              style={{
                borderColor: isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#00cfff40';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
              }}
            >
              {/* Cover image */}
              <div className="w-full h-48 overflow-hidden flex-shrink-0 relative">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                {/* Category badge */}
                <div
                  className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold font-body tracking-wide uppercase"
                  style={{ backgroundColor: post.categoryColor + 'ee', color: '#0a1628' }}
                >
                  {post.category}
                </div>
                {idx === 0 && (
                  <div
                    className="absolute top-3 right-3 px-2 py-1 rounded-full text-[9px] font-extrabold font-body tracking-widest uppercase"
                    style={{ backgroundColor: '#10b981', color: '#fff' }}
                  >
                    Destacado
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex flex-col flex-1 p-5">
                {/* Meta */}
                <div className={`flex items-center gap-2 text-[11px] font-body mb-3 ${metaColor}`}>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-calendar-line text-[11px]" />
                  </div>
                  <span>{new Date(post.date).toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  <span className="opacity-40">·</span>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-time-line text-[11px]" />
                  </div>
                  <span>{post.readTime} min</span>
                </div>

                {/* Title */}
                <h3
                  className={`font-heading text-base font-bold leading-snug mb-3 flex-1 transition-colors duration-200 group-hover:text-electric ${titleColor}`}
                >
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className={`font-body text-xs leading-relaxed line-clamp-2 mb-4 ${excerptColor}`}>
                  {post.excerpt}
                </p>

                {/* CTA link */}
                <div className="flex items-center gap-1.5 text-electric text-xs font-semibold font-body group-hover:gap-2.5 transition-all duration-200">
                  <span>Leer artículo</span>
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA banner */}
        <div
          className="relative rounded-2xl overflow-hidden p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6"
          style={{
            background: isLight
              ? 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%)'
              : 'linear-gradient(135deg, rgba(0,207,255,0.08) 0%, rgba(16,185,129,0.06) 100%)',
            border: '1px solid',
            borderColor: isLight ? 'rgba(0,207,255,0.2)' : 'rgba(0,207,255,0.15)',
          }}
        >
          {/* Left accent */}
          <div
            className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
            style={{ background: 'linear-gradient(to bottom, #00cfff, #10b981)' }}
          />

          <div className="pl-4 sm:pl-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 flex items-center justify-center" style={{ color: '#00cfff' }}>
                <i className="ri-lightbulb-flash-line text-sm" />
              </div>
              <span
                className="font-body text-xs font-bold tracking-widest uppercase"
                style={{ color: '#00cfff' }}
              >
                Centro de Conocimiento
              </span>
            </div>
            <h4 className={`font-heading text-xl sm:text-2xl font-extrabold mb-1 ${titleColor}`}>
              6 artículos sobre e-CF, DGII y facturación en RD
            </h4>
            <p className={`font-body text-sm ${subColor}`}>
              Guías prácticas, comparativas y casos reales para que tu negocio esté siempre al día.
            </p>
          </div>

          <Link
            to="/blog"
            className="flex-shrink-0 flex items-center gap-2.5 px-7 py-3.5 rounded-full font-body text-sm font-extrabold transition-all duration-200 hover:scale-105 active:scale-95 whitespace-nowrap cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, #00cfff 0%, #10b981 100%)',
              color: '#0a1628',
              boxShadow: '0 0 20px rgba(0,207,255,0.25)',
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-book-open-line text-sm" />
            </div>
            Ver todos los artículos
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
